import { Category } from './Category';
import { Content } from './Content';
import { MergeMissingResolve, MergeTypeMismatchResolve } from './MergeTypes';

export type CompareNodeStatus =
  | 'match'
  /** Branch pairs align, but at least one direct child does not. */
  | 'has-child-diff'
  | 'missing-content'
  | 'left-only'
  | 'right-only'
  | 'type-conflict'
  /** Child exists on one side only under a parent with branch/content type conflict. */
  | 'unpaired';

export interface DocumentCompareSide {
  label: string;
  documentName: string;
  categoryId: string;
  readAccessGrantId?: number;
}

/** Which document(s) already received a Keep merge for a type-conflict row (keyed by child path). */
export type TypeConflictMergeTarget = 'left' | 'right';

/** One reversible compare copy (content rows and/or categories created on the target document). */
export interface CompareCopyUndoEntry {
  targetDocumentName: string;
  createdContentIds: string[];
  createdCategoryIds: string[];
}

export interface DocumentCompareSession {
  tabId: string;
  left: DocumentCompareSide;
  right: DocumentCompareSide;
  leftRoot: Category;
  rightRoot: Category;
  resolveMissing: MergeMissingResolve;
  resolveTypeMismatch: MergeTypeMismatchResolve;
  typeConflictMergedSides?: Record<string, TypeConflictMergeTarget[]>;
  /** Stack of copy operations that can be undone from the compare tab (newest last). */
  copyUndoStack?: CompareCopyUndoEntry[];
}

export interface ContentCompareBuckets {
  both: Content[];
  leftOnly: Content[];
  rightOnly: Content[];
}

export function createDocumentCompareTabId(): string {
  return `compare-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isDocumentCompareTabId(tabId: string): boolean {
  return tabId.startsWith('compare-');
}

export function documentCompareTabLabel(leftLabel: string, rightLabel: string): string {
  return `Compare: ${leftLabel} ↔ ${rightLabel}`;
}

export type MergeCompareTypeConflictDirection = 'left' | 'right';

/** Which compare side is duplicated as the template for merge into a new document. */
export type CompareMergeNewDocumentBase = 'left' | 'right';

export interface MergeCompareCategorySubtreeTarget {
  mergeIntoCategoryId: string;
  sourceCategoryId: string;
  treatIntoAsParent: boolean;
}

export interface MergeCompareContentSelection {
  mergeCategorySubtreeToLeft?: MergeCompareCategorySubtreeTarget;
  mergeCategorySubtreeToRight?: MergeCompareCategorySubtreeTarget;
  copyToLeft?: {
    targetCategoryId?: string;
    targetParentCategoryId?: string;
    sourceContentIds?: string[];
    sourceCategoryIds?: string[];
    /** Create an empty branch shell on the target before copying sourceCategoryIds into it. */
    createBranchShell?: { parentCategoryId: string; name: string };
    /** Category created in the compare UI immediately before this copy (single-sided flow). */
    newlyCreatedCategoryId?: string;
  };
  copyToRight?: {
    targetCategoryId?: string;
    targetParentCategoryId?: string;
    sourceContentIds?: string[];
    sourceCategoryIds?: string[];
    createBranchShell?: { parentCategoryId: string; name: string };
    newlyCreatedCategoryId?: string;
  };
  typeConflict?: {
    direction: MergeCompareTypeConflictDirection;
    rows: {
      rowKey: string;
      leftCategoryId: string;
      rightCategoryId: string;
    }[];
  };
}

export function applyTypeConflictMergeRecord(
  existing: Record<string, TypeConflictMergeTarget[]> | undefined,
  rowKey: string,
  direction: MergeCompareTypeConflictDirection,
): Record<string, TypeConflictMergeTarget[]> {
  const merged = { ...(existing ?? {}) };
  const sides = new Set(merged[rowKey] ?? []);
  sides.add(direction);
  merged[rowKey] = Array.from(sides);
  return merged;
}

export function typeConflictMergeTargetsForRows(
  mergedSides: Record<string, TypeConflictMergeTarget[]> | undefined,
  rows: { key: string }[],
): { canLeft: boolean; canRight: boolean } {
  if (rows.length === 0) {
    return { canLeft: false, canRight: false };
  }
  let canLeft = true;
  let canRight = true;
  for (const row of rows) {
    const done = mergedSides?.[row.key] ?? [];
    if (done.includes('left')) {
      canLeft = false;
    }
    if (done.includes('right')) {
      canRight = false;
    }
  }
  return { canLeft, canRight };
}
